/**
 * AppNav — the bar above intake's pages.
 *
 * It carried no destinations until #85, and that was the shape of the
 * product rather than an unfinished bar: settings was the whole of this
 * app, and a Company had nothing to browse here because its jobs live in
 * whichever rental system it runs. That reasoning holds for jobs and
 * fails for everything upstream of one. What came in — every call, and
 * every form submission — is intake's own, and it is the one thing no
 * rental system will ever hold: the calls that became nothing, the
 * recording, the transcript, and anything that failed to draft.
 *
 * So there is one destination, and configuration is not among them:
 * Settings and Logout sit in the account menu, exactly as the RMS
 * workspace has them. The bar is chrome and settings is not a place an
 * Operator moves between.
 *
 * The account trigger is the account's own email because that is the one
 * thing worth stating on a screen that edits a Company's number and the
 * key its jobs draft through: a person who administers two Companies has
 * no other way to tell which one is in front of them.
 *
 * Built on Astryx `TopNav` for the bar's own metrics, with no wordmark in
 * the heading slot — the RMS workspace's copy explains why, and the same
 * reasoning survives the swap of hosts.
 *
 * The menu is hand-rolled rather than Astryx's `DropdownMenu`, whose
 * popover positions itself with CSS anchor positioning (`position-area`,
 * `anchor-size()`). Safari on iOS does not support that, so the panel
 * falls back to a fixed element with zeroed insets and lands in the
 * viewport's top-left corner. This uses the same bottom-right-anchored
 * panel as `HeaderActions` and `Select`, which positions everywhere.
 */
import { useRef, useState, type ComponentProps } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { TopNav, TopNavItem } from "@astryxdesign/core/TopNav";
import { ChevronDownIcon } from "./icons";
import { Softphone } from "../Softphone";
import { Texts } from "../Texts";
import { useOptionalAuth } from "../../lib/auth";
import { useDismissable } from "../../lib/use-dismissable";
import styles from "./AppNav.module.css";

interface Destination {
  to: string;
  label: string;
}

/**
 * Primary destinations. One today, and DESIGN.md caps the bar at five.
 *
 * It is called Inquiries, which is wider than **Inquiry** as CONTEXT.md
 * defines it — most of what this list holds became no Inquiry at all.
 * The collision is deliberate: the word a Company uses for "something
 * came in" is the word the destination gets, and the settings section it
 * took the name from is Intake now, so one name never means both a place
 * you read and a place you configure.
 */
export const DESTINATIONS: Destination[] = [
  { to: "/enquiries", label: "Enquiries" },
];

/** A destination is active on its own page and on its detail routes. */
export function isActiveDestination(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * Adapter so Astryx's link-shaped components (which pass `href`) render a
 * react-router `Link` (which takes `to`) — otherwise every nav click is a
 * full page load.
 */
function RouterLink({
  href = "/",
  ...rest
}: { href?: string } & Omit<ComponentProps<typeof Link>, "to">) {
  return <Link to={href} {...rest} />;
}

/** Settings + Logout. Configuration is not a destination, so it is not in the bar. */
function AccountMenu() {
  const navigate = useNavigate();
  const auth = useOptionalAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useDismissable({
    open,
    onDismiss: () => setOpen(false),
    onEscape: () => {
      setOpen(false);
      triggerRef.current?.focus();
    },
    pointer: [containerRef],
  });

  const items = [
    { label: "Settings", onSelect: () => void navigate("/settings") },
    {
      label: "Logout",
      onSelect: () => {
        void (async () => {
          await auth?.signOut();
          window.location.assign("/login");
        })();
      },
    },
  ];

  return (
    <div ref={containerRef} className={styles.accountMenu}>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((o) => !o)}
        className={styles.accountTrigger}
      >
        <span className={styles.accountEmail}>
          {auth?.user?.email ?? "Account"}
        </span>
        <ChevronDownIcon className={styles.accountChevron} />
      </button>
      {open && (
        <div role="menu" className={styles.menu}>
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
              className={styles.menuItem}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function AppNav() {
  const { pathname } = useLocation();

  return (
    <TopNav
      label="Main"
      startContent={DESTINATIONS.map((d) => (
        <TopNavItem
          key={d.to}
          label={d.label}
          href={d.to}
          as={RouterLink}
          isSelected={isActiveDestination(pathname, d.to)}
        />
      ))}
      endContent={
        <div className={styles.end}>
          {/* The phone lives in the bar so its registration outlives the
              page (#207). It draws nothing where there is no voice server. */}
          <Softphone />
          {/* Texts beside it, for the same reason: an open conversation
              outlives the page. Nothing where there is no carrier. */}
          <Texts />
          <AccountMenu />
        </div>
      }
    />
  );
}
